import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Pencil, Trash2, Send, CheckCircle, XCircle, Copy, Building2, User, Calendar, Mail, Download, Link } from "lucide-react"
import { useQuote, useDeleteQuote, useSendQuote, useAcceptQuote, useRejectQuote, useNewQuoteVersion } from "@/hooks/use-quotes"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { formatDate, formatCurrency } from "@/lib/utils"
import { toast } from "sonner"
import type { QuoteStatus } from "@/lib/types"

const STATUS_CONFIG: Record<QuoteStatus, { label: string; color: string; bg: string }> = {
  draft: { label: "Szkic", color: "#6B7280", bg: "rgba(107, 114, 128, 0.1)" },
  sent: { label: "Wyslana", color: "#3B82F6", bg: "rgba(59, 130, 246, 0.1)" },
  viewed: { label: "Wyswietlona", color: "#8B5CF6", bg: "rgba(139, 92, 246, 0.1)" },
  accepted: { label: "Zaakceptowana", color: "#10B981", bg: "rgba(16, 185, 129, 0.1)" },
  rejected: { label: "Odrzucona", color: "#EF4444", bg: "rgba(239, 68, 68, 0.1)" },
  expired: { label: "Wygasla", color: "#F59E0B", bg: "rgba(245, 158, 11, 0.1)" },
  cancelled: { label: "Anulowana", color: "#6B7280", bg: "rgba(107, 114, 128, 0.1)" },
}

export function QuoteDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useQuote(id)
  const deleteMutation = useDeleteQuote()
  const sendMutation = useSendQuote()
  const acceptMutation = useAcceptQuote()
  const rejectMutation = useRejectQuote()
  const newVersionMutation = useNewQuoteVersion()

  const quote = data?.data

  const handleDelete = async () => {
    if (!id || !confirm("Czy na pewno chcesz usunac te oferte?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Oferta usunieta")
    navigate("/quotes")
  }

  const handleSend = async () => {
    if (!id) return
    await sendMutation.mutateAsync(id)
    toast.success("Oferta wyslana")
  }

  const handleAccept = async () => {
    if (!id) return
    await acceptMutation.mutateAsync(id)
    toast.success("Oferta zaakceptowana")
  }

  const handleReject = async () => {
    if (!id) return
    await rejectMutation.mutateAsync(id)
    toast.success("Oferta odrzucona")
  }

  const handleNewVersion = async () => {
    if (!id) return
    const res = await newVersionMutation.mutateAsync(id)
    toast.success("Utworzono nowa wersje")
    navigate(`/quotes/${res.data.id}`)
  }

  const handleDownloadPdf = () => {
    if (!id) return
    window.open(`/api/v1/quotes/${id}/pdf`, "_blank")
  }

  const handleCopyPublicLink = () => {
    if (!quote?.signature_token) return
    const url = `${window.location.origin}/quotes/view/${quote.signature_token}`
    navigator.clipboard.writeText(url)
    toast.success("Link do oferty skopiowany")
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!quote) {
    return <div className="text-muted-foreground">Nie znaleziono oferty</div>
  }

  const statusCfg = STATUS_CONFIG[quote.status]
  const fmt = (n: number) => new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/quotes")} className="rounded-md p-1.5 transition-colors hover:bg-muted/30 text-muted-foreground">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="page-title">{quote.number}</h1>
              {quote.version > 1 && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  v{quote.version}
                </span>
              )}
              <Badge variant="outline" style={{ color: statusCfg.color, backgroundColor: statusCfg.bg, borderColor: statusCfg.color }}>
                {statusCfg.label}
              </Badge>
            </div>
            {quote.company && (
              <p className="text-sm text-muted-foreground">
                <Building2 size={12} className="inline mr-1" />{quote.company.name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {quote.status === "draft" && (
            <Button variant="outline" size="sm" onClick={handleSend}>
              <Send size={14} className="mr-1" /> Wyslij
            </Button>
          )}
          {(quote.status === "sent" || quote.status === "viewed") && (
            <>
              <Button variant="outline" size="sm" onClick={handleAccept} className="text-green-600 border-green-200 hover:bg-green-50">
                <CheckCircle size={14} className="mr-1" /> Zaakceptuj
              </Button>
              <Button variant="outline" size="sm" onClick={handleReject} className="text-red-600 border-red-200 hover:bg-red-50">
                <XCircle size={14} className="mr-1" /> Odrzuc
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
            <Download size={14} className="mr-1" /> PDF
          </Button>
          {quote.signature_token && (
            <Button variant="outline" size="sm" onClick={handleCopyPublicLink}>
              <Link size={14} className="mr-1" /> Link
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleNewVersion}>
            <Copy size={14} className="mr-1" /> Nowa wersja
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/quotes/${id}/edit`)}>
            <Pencil size={14} className="mr-1" /> Edytuj
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600">
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <Tabs defaultValue="items">
            <TabsList>
              <TabsTrigger value="items">Pozycje ({quote.items.length})</TabsTrigger>
              <TabsTrigger value="details">Szczegoly</TabsTrigger>
            </TabsList>

            <TabsContent value="items" className="mt-4">
              <div className="surface-card">
                {quote.items.length === 0 ? (
                  <p className="p-8 text-center text-sm text-muted-foreground">Brak pozycji</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Pozycja</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Ilosc</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Cena netto</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Rabat</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">VAT</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Netto</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Brutto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quote.items.map((item) => (
                        <tr key={item.id} className="border-t">
                          <td className="px-4 py-2">
                            <span className="text-foreground">{item.name}</span>
                            {item.description && (
                              <p className="text-xs mt-0.5 text-muted-foreground">{item.description}</p>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                            {item.quantity} {item.unit}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                            {fmt(item.unit_price_net)}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                            {item.discount_percent > 0 ? `${item.discount_percent}%` : "\u2014"}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                            {item.vat_rate}%
                          </td>
                          <td className="px-4 py-2 text-right font-mono font-medium text-foreground">
                            {fmt(item.line_total_net)}
                          </td>
                          <td className="px-4 py-2 text-right font-mono font-medium text-foreground">
                            {fmt(item.line_total_gross)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted">
                        <td colSpan={5} className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Suma netto:</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold text-foreground">{formatCurrency(quote.total_net, quote.currency)}</td>
                        <td></td>
                      </tr>
                      <tr className="bg-muted">
                        <td colSpan={5} className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">VAT:</td>
                        <td className="px-4 py-2 text-right font-mono text-muted-foreground">{formatCurrency(quote.total_vat, quote.currency)}</td>
                        <td></td>
                      </tr>
                      <tr className="border-t bg-muted">
                        <td colSpan={5} className="px-4 py-2 text-right font-semibold text-foreground">Suma brutto:</td>
                        <td colSpan={2} className="px-4 py-2 text-right font-mono text-base font-bold text-primary">
                          {formatCurrency(quote.total_gross, quote.currency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </TabsContent>

            <TabsContent value="details" className="mt-4 space-y-4">
              {quote.notes && (
                <div className="surface-card p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">Uwagi</h3>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{quote.notes}</p>
                </div>
              )}
              {quote.terms && (
                <div className="surface-card p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">Warunki</h3>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{quote.terms}</p>
                </div>
              )}
              {!quote.notes && !quote.terms && (
                <p className="text-center text-sm py-8 text-muted-foreground">
                  Brak dodatkowych szczegolow
                </p>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Value card */}
          <div className="surface-card p-4 space-y-3">
            <div>
              <span className="text-xs text-muted-foreground">Wartosc netto</span>
              <p className="text-lg font-mono font-semibold text-foreground">
                {formatCurrency(quote.total_net, quote.currency)}
              </p>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Wartosc brutto</span>
              <p className="text-xl font-mono font-bold text-primary">
                {formatCurrency(quote.total_gross, quote.currency)}
              </p>
            </div>
          </div>

          {/* Info card */}
          <div className="surface-card p-4 space-y-3">
            {quote.company && (
              <div>
                <span className="text-xs text-muted-foreground">Kontrahent</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <Building2 size={14} className="text-muted-foreground" />
                  <button
                    onClick={() => navigate(`/companies/${quote.company!.id}`)}
                    className="text-sm hover:underline text-primary"
                  >
                    {quote.company.name}
                  </button>
                </div>
              </div>
            )}
            {quote.deal && (
              <div>
                <span className="text-xs text-muted-foreground">Szansa</span>
                <button
                  onClick={() => navigate(`/deals/${quote.deal!.id}`)}
                  className="block text-sm hover:underline mt-0.5 text-primary"
                >
                  {quote.deal.name}
                </button>
              </div>
            )}
            {quote.owner && (
              <div>
                <span className="text-xs text-muted-foreground">Wlasciciel</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <User size={14} className="text-muted-foreground" />
                  <p className="text-sm text-foreground">{quote.owner.first_name} {quote.owner.last_name}</p>
                </div>
              </div>
            )}
            {quote.recipient_name && (
              <div>
                <span className="text-xs text-muted-foreground">Odbiorca</span>
                <p className="text-sm text-foreground">{quote.recipient_name}</p>
              </div>
            )}
            {quote.recipient_email && (
              <div>
                <span className="text-xs text-muted-foreground">Email odbiorcy</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <Mail size={14} className="text-muted-foreground" />
                  <p className="text-sm text-foreground">{quote.recipient_email}</p>
                </div>
              </div>
            )}
            <div className="border-t pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-muted-foreground">Data wystawienia</span>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Calendar size={12} className="text-muted-foreground" />
                    <p className="text-sm text-foreground">{quote.issue_date ? formatDate(quote.issue_date) : "\u2014"}</p>
                  </div>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Wazna do</span>
                  <p className="text-sm text-foreground">{quote.valid_until ? formatDate(quote.valid_until) : "\u2014"}</p>
                </div>
              </div>
            </div>
            {quote.sent_at && (
              <div>
                <span className="text-xs text-muted-foreground">Wyslana</span>
                <p className="text-sm text-foreground">{formatDate(quote.sent_at)}</p>
              </div>
            )}
            {quote.accepted_at && (
              <div>
                <span className="text-xs text-muted-foreground">Zaakceptowana</span>
                <p className="text-sm text-emerald-500">{formatDate(quote.accepted_at)}</p>
              </div>
            )}
            {quote.rejected_at && (
              <div>
                <span className="text-xs text-muted-foreground">Odrzucona</span>
                <p className="text-sm text-red-500">{formatDate(quote.rejected_at)}</p>
              </div>
            )}
            <div className="border-t pt-3">
              <span className="text-xs text-muted-foreground">Utworzono</span>
              <p className="text-sm text-foreground">{formatDate(quote.created_at)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
